import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics, sendCollectionToAnalytics } from "@/utils/analytics";
import { LIST_SERVICE_TAG } from "@/shared/store/service/commonServiceTags";
import { showNotification } from "@/redux/actions/notification";
import { requestCloseModal } from "@/redux/actions/modal";
import {
  LENDER_HUB_DEAL_REDUCER_PATH,
  LENDER_HUB_DEAL_RELATED_TAGS,
  LENDER_HUB_DEAL_SERVICE_TAG,
} from "@/features/LenderHub/Deal/utils/constants/lenderHubDealServiceTags";
import { getQueryParams } from "@/utils/httpRequest";
import { setProcessingState } from "@/redux/reducers/modalConfirmation";
import { lenderHubProfileService } from "@/features/LenderHub/Profile/services/profileService";

export const lenderHubDealService = createApi({
  reducerPath: LENDER_HUB_DEAL_REDUCER_PATH,
  tagTypes: LENDER_HUB_DEAL_RELATED_TAGS,
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/capworks/",
    credentials: "include",
  }),
  endpoints: builder => ({
    getDeals: builder.query({
      query: params => `deals?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              ...result.items.map(({ lenderBoardId }) => ({
                type: LENDER_HUB_DEAL_SERVICE_TAG,
                id: lenderBoardId,
              })),
              { type: LENDER_HUB_DEAL_SERVICE_TAG, id: LIST_SERVICE_TAG },
            ]
          : [{ type: LENDER_HUB_DEAL_SERVICE_TAG, id: LIST_SERVICE_TAG }],
    }),
    getRequests: builder.query({
      query: params => `deals/requests?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              ...result.items.map(({ lenderBoardId }) => ({
                type: LENDER_HUB_DEAL_SERVICE_TAG,
                id: lenderBoardId,
              })),
              { type: LENDER_HUB_DEAL_SERVICE_TAG, id: LIST_SERVICE_TAG },
            ]
          : [{ type: LENDER_HUB_DEAL_SERVICE_TAG, id: LIST_SERVICE_TAG }],
    }),
    getMarketplaceDeals: builder.infiniteQuery({
      query: (queryArg, api = {}) => {
        // Handle the nested queryArg structure from RTK Query infiniteQuery
        const actualArgs = queryArg.queryArg || queryArg;

        // RTK Query passes pageParam in the top-level queryArg for subsequent calls
        const pageParam = queryArg.pageParam || api.pageParam || 1;

        // Remove pageParam and queryArg from params to avoid URL pollution
        const cleanParams = { ...actualArgs };
        delete cleanParams.pageParam;
        delete cleanParams.queryArg;

        const queryString = getQueryParams({ ...cleanParams, page: pageParam });
        return `marketplace/deals?${queryString}`;
      },
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage) {
            return undefined;
          }

          const currentPage = lastPageParam || lastPage.page || 1;
          const { totalPages } = lastPage;

          if (!totalPages) {
            return undefined;
          }

          // Stop if we've reached the total pages or if the last page has no records
          if (currentPage >= totalPages || !lastPage.records?.length) {
            return undefined;
          }

          return currentPage + 1;
        },
      },
      providesTags: result =>
        result
          ? [
              ...(result.pages?.flatMap(
                page =>
                  page.records?.map(({ loanId }) => ({
                    type: LENDER_HUB_DEAL_SERVICE_TAG,
                    id: loanId,
                  })) || [],
              ) || []),
              { type: LENDER_HUB_DEAL_SERVICE_TAG, id: LIST_SERVICE_TAG },
            ]
          : [{ type: LENDER_HUB_DEAL_SERVICE_TAG, id: LIST_SERVICE_TAG }],
    }),
    getDeal: builder.query({
      query: dealId => `deals/${dealId}`,
      providesTags: (result, error, arg) => [{ type: LENDER_HUB_DEAL_SERVICE_TAG, id: arg }],
    }),
    sendConnectionRequest: builder.mutation({
      query: body => ({
        url: `marketplace/deals/connection`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: LENDER_HUB_DEAL_SERVICE_TAG, id: LIST_SERVICE_TAG },
        { type: LENDER_HUB_DEAL_SERVICE_TAG, id: arg },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.CAPWORKS_MARKETPLACE_DEAL_CONNECTION_REQUEST_SENT, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description:
                  "Deal has been added to your pipeline, you can now see more details and interact with the broker.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(requestCloseModal());
          dispatch(lenderHubProfileService.util.invalidateTags(["LenderHubProfile"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    addToPipeline: builder.mutation({
      query: body => ({
        url: `marketplace/deals/pipeline`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: LENDER_HUB_DEAL_SERVICE_TAG, id: LIST_SERVICE_TAG },
        { type: LENDER_HUB_DEAL_SERVICE_TAG, id: arg },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.CAPWORKS_MARKETPLACE_DEAL_ADDED_TO_PIPELINE, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description:
                  "Deal has been added to your pipeline, you can now see more details and interact with the broker.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(requestCloseModal());
          dispatch(lenderHubProfileService.util.invalidateTags(["LenderHubProfile"]));
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    sendMarketplaceDealReminder: builder.mutation({
      query: body => ({
        url: `marketplace/deals/connection/reminder`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: LENDER_HUB_DEAL_SERVICE_TAG, id: LIST_SERVICE_TAG },
        { type: LENDER_HUB_DEAL_SERVICE_TAG, id: arg },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.CAPWORKS_MARKETPLACE_DEAL_REMINDER_SENT, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Reminder has been sent to the broker.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    acceptDeal: builder.mutation({
      query: dealId => ({
        url: `deals/${dealId}/accept`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: LENDER_HUB_DEAL_SERVICE_TAG, id: LIST_SERVICE_TAG },
        { type: LENDER_HUB_DEAL_SERVICE_TAG, id: arg },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_HUB_DEAL_ACCEPTED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description:
                  "Deal has been added to your pipeline, you can now see more details and interact with the broker.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
          dispatch(lenderHubProfileService.util.invalidateTags(["LenderHubProfile"]));
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),
    closeDeal: builder.mutation({
      query: ({ lenderBoardId: dealId, ...body }) => ({
        url: `deals/${dealId}/close`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: LENDER_HUB_DEAL_SERVICE_TAG, id: LIST_SERVICE_TAG },
        { type: LENDER_HUB_DEAL_SERVICE_TAG, id: arg.lenderBoardId },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_HUB_DEAL_CLOSED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Deal has been closed.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
          dispatch(lenderHubProfileService.util.invalidateTags(["LenderHubProfile"]));
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),
    reopenDeal: builder.mutation({
      query: dealId => ({
        url: `deals/${dealId}/reopen`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: LENDER_HUB_DEAL_SERVICE_TAG, id: LIST_SERVICE_TAG },
        { type: LENDER_HUB_DEAL_SERVICE_TAG, id: arg },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_HUB_DEAL_REOPENED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Deal has been reopened.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
          dispatch(lenderHubProfileService.util.invalidateTags(["LenderHubProfile"]));
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),
  }),
});

export const {
  useGetDealsQuery,
  useGetRequestsQuery,
  useGetMarketplaceDealsInfiniteQuery,
  useGetDealQuery,
  useAcceptDealMutation,
  useCloseDealMutation,
  useReopenDealMutation,
  useSendConnectionRequestMutation,
  useAddToPipelineMutation,
  useSendMarketplaceDealReminderMutation,
} = lenderHubDealService;
